import { useEffect, useState } from "react";
import { Link, Navigate, useParams, useSearchParams } from "react-router";
import { formatVnd } from "../../shared/i18n/index.ts";
import { formatPhone } from "../../shared/phone.ts";
import { ApiError } from "../lib/api.ts";
import { serverNow, syncClock } from "../lib/clock.ts";
import { useI18n } from "../lib/i18n.tsx";
import { useMe } from "../lib/me.tsx";
import { act, enterCode, loadPurchase, type PurchaseView } from "../lib/purchase-api.ts";

/** /p/:id — buyer or seller view of one purchase (08-design.md §Purchase, §Sale). */
export function Purchase() {
  const { id = "" } = useParams();
  const { t, locale } = useI18n();
  const { me, loading } = useMe();
  const [params] = useSearchParams();
  const [p, setP] = useState<PurchaseView | null>(null);
  const [missing, setMissing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [code, setCode] = useState("");
  const [, setTick] = useState(0);

  useEffect(() => {
    if (!me) return;
    let live = true;
    loadPurchase(id)
      .then((v) => {
        if (!live) return;
        syncClock(v.server_ts);
        setP(v);
      })
      .catch(() => live && setMissing(true));
    const tick = setInterval(() => setTick((n) => n + 1), 30_000);
    return () => {
      live = false;
      clearInterval(tick);
    };
  }, [id, me]);

  if (loading) return <div className="page muted">{t("common.loading")}</div>;
  if (!me) return <Navigate to={`/login?next=/p/${id}`} replace />;
  if (missing) return <div className="page muted">{t("error.not_found")}</div>;
  if (!p) return <div className="page muted">{t("common.loading")}</div>;

  const now = serverNow();
  const hoursLeft = Math.max(0, Math.ceil((p.contact_share_deadline - now) / 3600));
  const usual = p.seller.median_share_s
    ? Math.max(1, Math.round(p.seller.median_share_s / 3600))
    : null;
  const other = p.role === "buyer" ? p.seller : p.buyer;
  const otherHandle = other.handle;

  async function run(fn: () => Promise<PurchaseView>) {
    setError(null);
    try {
      setP(await fn());
      setConfirmCancel(false);
    } catch (e) {
      if (e instanceof ApiError && e.body.error === "wrong")
        setError(t("deal.wrong", { n: e.body.attempts_left ?? 0 }));
      else if (e instanceof ApiError && e.body.error === "locked") setError(t("deal.locked"));
      else setError(t("error.generic"));
      setCode("");
    }
  }

  const steps: { key: string; label: string; done: boolean }[] = [
    { key: "bought", label: t("purchase.timeline.bought"), done: true },
    { key: "waiting", label: t("purchase.timeline.waiting"), done: p.status !== "pending_contact" },
    {
      key: "shared",
      label: t("purchase.timeline.shared"),
      done: p.status === "contact_shared" || p.status === "completed",
    },
    { key: "completed", label: t("purchase.timeline.completed"), done: p.status === "completed" },
  ];

  return (
    <div className="page page--narrow" data-testid="purchase-page">
      {params.get("bought") ? (
        <p className="ok" role="status">
          {t("buy.success", { seller: p.seller.handle, hours: usual ?? 24 })}
        </p>
      ) : null}
      <div className="rowlink">
        <div className="rowlink__cover">
          {p.listing?.cover ? <img src={p.listing.cover} alt="" /> : null}
        </div>
        <div className="rowlink__body">
          <Link to={p.listing ? `/l/${p.listing.id}` : "#"} className="rowlink__title">
            {p.listing?.title}
          </Link>
          <div className="hint">
            {p.quantity} × {formatVnd(p.unit_price)} · <strong>{formatVnd(p.total)}</strong>
          </div>
        </div>
      </div>

      {p.status === "cancelled" ? (
        <p className="ended" data-testid="purchase-status">
          {t("purchase.status.cancelled")}
        </p>
      ) : (
        <ol className="timeline" data-testid="timeline">
          {steps.map((s) => (
            <li key={s.key} className={`timeline__step${s.done ? " is-done" : ""}`}>
              {s.label}
            </li>
          ))}
        </ol>
      )}
      {p.problem_open ? <div className="banner banner--warn">{t("problem.open_badge")}</div> : null}
      {error ? (
        <p className="error" role="alert">
          {error}
        </p>
      ) : null}

      {/* other party */}
      <section className="section">
        <h2 className="h2">
          {p.role === "buyer" ? t("profile.as_seller") : t("profile.as_buyer")}
        </h2>
        <p>
          <Link to={`/u/${otherHandle}`}>@{otherHandle}</Link>
          {p.role === "seller" ? (
            <span className="hint">
              {" "}
              · {t("profile.bought", { n: p.buyer.completed_purchases })} ·{" "}
              {p.buyer.line === "reliable" ? (
                <span className="tag tag--ok">{t("buyer.reliable")}</span>
              ) : p.buyer.line === "noshows" ? (
                <span className="error">{t("buyer.noshows", { n: p.buyer.no_shows_90d })}</span>
              ) : (
                t("buyer.new")
              )}
            </span>
          ) : null}
        </p>
        {other.phone ? (
          <div className="row" data-testid="contact">
            <a className="btn" href={`tel:${other.phone}`}>
              {t("purchase.call")} {formatPhone(other.phone)}
            </a>
            <a
              className="btn"
              href={`https://zalo.me/${other.phone.replace(/^\+/, "")}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              {t("purchase.zalo")}
            </a>
          </div>
        ) : null}
        {p.role === "seller" && p.buyer.district ? (
          <p className="hint">
            {p.buyer.district}
            {p.buyer.city
              ? `, ${locale === "vi" ? p.buyer.city.name_vi : p.buyer.city.name_en}`
              : ""}
          </p>
        ) : null}
      </section>

      {/* pending: waiting / share / cancel */}
      {p.status === "pending_contact" ? (
        <section className="section">
          {p.role === "buyer" ? (
            <p className="muted">
              {usual ? t("purchase.usual_response", { hours: usual }) : t("purchase.max_response")}
            </p>
          ) : (
            <>
              <p className="muted">{t("purchase.deadline_left", { hours: hoursLeft })}</p>
              <button
                className="btn btn--accent btn--block"
                onClick={() => void run(() => act(p.id, "share"))}
                data-testid="share-contact"
              >
                {t("purchase.share_contact")}
              </button>
            </>
          )}
          {!confirmCancel ? (
            <button
              className="btn btn--ghost"
              onClick={() => setConfirmCancel(true)}
              data-testid="cancel-open"
            >
              {t("purchase.cancel")}
            </button>
          ) : (
            <div className="sheet" data-testid="strike-warning">
              <p>{t("cancel.warning")}</p>
              <div className="row">
                <button
                  className="btn btn--danger"
                  onClick={() => void run(() => act(p.id, "cancel"))}
                  data-testid="cancel-confirm"
                >
                  {t("purchase.cancel")}
                </button>
                <button className="btn btn--ghost" onClick={() => setConfirmCancel(false)}>
                  {t("common.back")}
                </button>
              </div>
            </div>
          )}
        </section>
      ) : null}

      {/* shared: deal code, mark done, confirm, problem */}
      {p.status === "contact_shared" ? (
        <section className="section">
          <p className="ok">{t("purchase.contact.shared")}</p>
          {p.role === "buyer" && p.deal_code ? (
            <>
              <h2 className="h2">{t("deal.code")}</h2>
              <div className="dealcode" aria-label={t("deal.code")} data-testid="deal-code">
                {p.deal_code.split("").map((d, i) => (
                  <span key={i}>{d}</span>
                ))}
              </div>
              <p className="muted small">{t("deal.code.hint")}</p>
            </>
          ) : null}
          {p.role === "seller" ? (
            <form
              className="form"
              onSubmit={(e) => {
                e.preventDefault();
                if (code.length === 4) void run(() => enterCode(p.id, code));
              }}
            >
              <h2 className="h2">{t("deal.enter.cta")}</h2>
              <p className="muted small">{t("deal.enter")}</p>
              <input
                className="input input--otp"
                inputMode="numeric"
                maxLength={4}
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 4))}
                aria-label={t("deal.code")}
                data-testid="deal-input"
              />
              <button
                className="btn btn--accent"
                type="submit"
                disabled={code.length !== 4}
                data-testid="deal-submit"
              >
                {t("common.confirm")}
              </button>
            </form>
          ) : null}
          <div className="row" style={{ marginTop: 12 }}>
            {p.done_marked_by === null ? (
              <button
                className="btn"
                onClick={() => void run(() => act(p.id, "done"))}
                data-testid="mark-done"
              >
                {t("purchase.mark_done")}
              </button>
            ) : p.done_marked_by === "other" ? (
              <button
                className="btn btn--accent"
                onClick={() => void run(() => act(p.id, "confirm"))}
                data-testid="confirm-done"
              >
                {t("purchase.confirm_done")}
              </button>
            ) : (
              <span className="muted small">{t("purchase.mark_done")} ✓</span>
            )}
            <Link className="btn btn--ghost" to={`/p/${p.id}/problem`}>
              {t("purchase.report_problem")}
            </Link>
          </div>
        </section>
      ) : null}

      {p.status === "completed" ? (
        <section className="section">
          <p className="ok" data-testid="purchase-status">
            {t("deal.completed")}
          </p>
          {p.role === "buyer" ? (
            <p>
              {t("invite.ask", { seller: p.seller.handle })}{" "}
              <Link className="btn btn--ghost" to={`/p/${p.id}/invite`}>
                {t("invite.ask.cta")}
              </Link>
            </p>
          ) : null}
          <Link className="btn btn--ghost" to={`/p/${p.id}/problem`}>
            {t("purchase.report_problem")}
          </Link>
        </section>
      ) : null}
      <p>
        <Link className="btn btn--ghost" to={p.role === "buyer" ? "/me/purchases" : "/me/sales"}>
          {t("common.back")}
        </Link>
      </p>
    </div>
  );
}
